/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from 'componentregistry/registry/Base.type';
import { toolTipProps } from 'componentregistry/utils/commonProps';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { isEmpty } from 'utils/isEmpty';
import { CommonProps } from '../../layout/Sidebar/panels/PropertiesView/CommonProperties';
import { Platform } from '../../platform/Platform';
import {
  ComponentItemData,
  ComponentManager,
  PropertyContext,
  PropertyDefinitions,
  RequiredItem,
  RequiredItemData,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import {
  typographyAlign,
  typographyDisplay,
  typographyVariant,
} from '../../registry/constants';
import { TextItem } from '../Text/Text';
import { baseFieldProps } from '../TextField/BaseField';
import {
  defaultValue,
  Typography,
  TypographyItem,
  Variant,
} from './Typography';
import { helpRenders } from './Typography_Help';

const props: PropertyDefinitions<Omit<Typography, keyof CommonProps>> = {
  text: {
    label: 'Text',
    type: 'expression',
    returnType: async () => 'string',
  },
  variant: {
    label: 'Variant',
    type: 'select',
    options: typographyVariant,
  },
  fontSize: {
    label: 'Font Size',
    type: 'string',
  },
  fontWeight: {
    label: 'Font Weigth',
    type: 'number',
    min: 100,
    max: 900,
    step: 100,
  },
  fontFamily: {
    label: 'Font Family',
    type: 'string',
  },
  fontStyle: {
    label: 'Font Style',
    type: 'select',
    options: toOptions(['italic', 'normal', 'oblique']),
  },
  align: {
    label: 'Align',
    type: 'select',
    options: typographyAlign,
  },
  color: {
    label: 'Color',
    type: 'ColorExpression',
  },
  display: {
    label: 'Display',
    type: 'select',
    options: typographyDisplay,
  },
  noWrap: {
    label: 'No Wrap',
    type: 'boolean',
    helpText:
      'If checked, the text will not wrap, but instead will truncate with a text overflow ellipsis.',
  },
  maxLinesForNoWrap: {
    label: 'Max Lines For No Wrap',
    type: 'number',
    min: 1,
    max: 10,
    step: 1,
    helpText:
      'The maximum number of lines to display before truncating with an ellipsis.',
  },
  paragraph: {
    label: 'Paragraph',
    type: 'boolean',
    helpText: 'If checked, the text will have a bottom margin.',
  },
  padding: {
    label: 'Padding',
    type: 'padding',
  },
  viewAttribute: {
    ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
    required: false,
    getAttributeDataTypes: () => 'ALL',
  },
  optionValues: {
    type: 'options',
    label: 'Option Values',
    category: 'Select Options',
    helpText:
      'If the options are fixed, then you can specify here instead of providing a Data Store Alias below. e.g. B:Blue, G:Green, Yellow, Red',
  },
  comboDatasourceAlias: {
    label: 'Data Store Alias',
    type: 'DataSourceAliasChooser',
    category: 'Select Options',
    allowedTypes: ['CloudDataStore', 'ReadOnlyDataStore'],
    helpText:
      'Only CloudDataStore and ReadOnlyDataStore are allowed. Use Query Select action to query it externally.',
  },
  valueAttribute: {
    label: 'Value Attribute',
    type: 'ComboAttributeChooser',
    aliasType: 'LOV',
    category: 'Select Options',
    getAttributeDataTypes: () => 'ALL',
    required: (item: RequiredItem<'Typography'>) =>
      !!item.props.comboDatasourceAlias,
  },
  labelAttribute: {
    aliasType: 'LOV',
    getAttributeDataTypes: () => 'ALL',
    label: 'Label Attribute',
    category: 'Select Options',
    type: 'ComboAttributeChooser',
    required: (item: RequiredItem<'Typography'>) =>
      !!item.props.comboDatasourceAlias,
  },
  doNotPrefetch: {
    label: 'Do Not Prefetch',
    type: 'boolean',
    category: 'Select Options',
  },
  lookup: {
    label: 'Lookup',
    type: 'LookupCodeChooser',
    category: 'Select Options',
  },
  ...toolTipProps,
};

const replaceableWith: ItemType[] = [
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'Typography',
];

type H1 = 'H1';

const isPropertyVisible: <KK extends keyof ComponentItemData<'Typography'>>(
  property: KK,
  itemProps: RequiredItemData<'Typography'>,
  context: PropertyContext,
) => boolean = (property, itemProps) => {
  if (property === 'maxLinesForNoWrap') {
    return itemProps.noWrap;
  }
  switch (property) {
    case 'optionValues':
      return (
        isEmpty(itemProps.lookup.code) &&
        isEmpty(itemProps.comboDatasourceAlias)
      );
    case 'comboDatasourceAlias':
      return isEmpty(itemProps.lookup.code) && isEmpty(itemProps.optionValues);
    case 'valueAttribute':
    case 'labelAttribute':
    case 'doNotPrefetch':
      return !isEmpty(itemProps.comboDatasourceAlias);
    case 'lookup':
      return (
        isEmpty(itemProps.comboDatasourceAlias) &&
        isEmpty(itemProps.optionValues)
      );
  }
  return true;
};

const H1Manager = (platform: Platform, type: H1): ComponentManager<H1> => {
  const variant: Variant = 'h1';
  const mgr: ComponentManager<H1> = {
    ...baseComponentManager<H1>(platform, type),
    defaultValue: { ...defaultValue, variant },
    props,
    getDisplayText: (item) => item.props.text.value,
    name: type,
    category: 'Typography',
    icon: { iconName: 'io-h1', iconType: 'cloudio' },
    isPropertyVisible,
    replaceableWith,
    description: `**Overview**<br/>
The H1 component represents a Level 1 Heading, the highest-ranking heading in the typography hierarchy. It renders text with the h1 variant style, making it the largest and most prominent text element available.
Use this component to declare the main title of a page or the primary subject of a major section.

**1. Typography & Styling**<br/>
* **Variant:** Automatically defaults to 'h1', ensuring consistent styling with the application's design system.
* **Customization:** Supports extensive styling options including Font Size, Font Weight (100-900), Font Family, Color, and Align (left, center, right, justify).

**2. Layout Control**<br/>
* **Wrapping:** Includes a No Wrap toggle to truncate long titles with an ellipsis (...) instead of breaking into multiple lines.
* **Spacing:** The Paragraph property adds a bottom margin to visually separate the heading from the content below it.

**3. Data Integration**<br/>
* **Dynamic Text:** The Text property is an expression, allowing the heading to display static text or dynamic values from data sources (e.g., Hello, {User.Name}).
* **Lookups:** Can optionally fetch display values from a Lookup Code or Data Store Alias if the heading represents a coded value (like a status ID).`,
    helpRenders: helpRenders('H1'),
  };
  return mgr;
};

type H2 = 'H2';

const H2Manager = (platform: Platform, type: H2): ComponentManager<H2> => {
  const variant: Variant = 'h2';
  const mgr: ComponentManager<H2> = {
    ...baseComponentManager<H2>(platform, type),
    defaultValue: { ...defaultValue, variant },
    props,
    getDisplayText: (item) => item.props.text.value,
    name: type,
    category: 'Typography',
    icon: { iconName: 'io-h2', iconType: 'cloudio' },
    isPropertyVisible,
    replaceableWith,
    description: `**Overview**<br/>
The H2 component is the second level in the typography hierarchy, serving as a primary subheading. It allows you to divide main content areas into distinct chapters or sections. In the platform registry, it is managed by the H2Manager, which standardizes its behavior and ensures it always renders with the specific 'h2' style variant.

**1. Fixed Configuration**<br/>
* **Variant Enforcement:** The component is explicitly defined with const variant: Variant = 'h2'. This value is merged into defaultValue, ensuring that every new H2 instance automatically applies the correct theme scaling and font weight for a Level 2 heading without manual adjustment.

**2. Designer Integration**<br/>
* **Display Text:** The manager implements getDisplayText: (item) => item.props.text.value, which allows the UI designer canvas to show the actual text content of the heading in the tree view or outline, making it easier to identify specific headings in a complex page structure.
* **Category:** It is grouped under the "Typography" category in the component palette.

**3. Dynamic Logic**<br/>
* **Property Visibility:** It utilizes the isPropertyVisible function to dynamically show or hide configuration fields. For example, if a Data Store alias is selected, the static optionValues field might be hidden to prevent conflicting configurations.
* **Replacement Strategy:** The component defines replaceableWith (referencing standard headers like H1, H3, etc.). This feature allows developers to instantly morph an H2 into an H1 or H3 via the properties panel if the semantic structure of the page changes, preserving the text and data bindings.

**4. Help & Documentation**<br/>
* **Embedded Help:** The manager includes helpRenders('H2'), suggesting it provides inline context or visual examples within the property editor to assist developers in selecting the correct heading level.`,
    helpRenders: helpRenders('H2'),
  };
  return mgr;
};

type H3 = 'H3';

const H3Manager = (platform: Platform, type: H3): ComponentManager<H3> => {
  const variant: Variant = 'h3';
  const mgr: ComponentManager<H3> = {
    ...baseComponentManager<H3>(platform, type),
    defaultValue: { ...defaultValue, variant },
    props,
    getDisplayText: (item) => item.props.text.value,
    name: type,
    category: 'Typography',
    icon: { iconName: 'io-h3', iconType: 'cloudio' },
    isPropertyVisible,
    replaceableWith,
    description: `**Overview**<br/>
The H3 component serves as a Level 3 Heading, designed to structure content nested inside H2 sections. It is managed by the H3Manager, which configures it to act as a sub-section header suitable for widgets, grid titles, and distinct topics within larger content blocks.

**1. Typography & Styling**<br/>
* **Variant:** The manager hardcodes const variant: Variant = 'h3', ensuring that all instances of this component render with the platform's standardized H3 styling (size, weight, line height) by default.
* **Visual Hierarchy:** It is tuned to provide a balanced font size—distinct enough to serve as a clear header, but compact enough for frequent use in dense UI layouts like dashboards.

**2. Component Behavior**<br/>
* **Base Inheritance:** It extends baseComponentManager, inheriting core platform capabilities like lifecycle management and standard event handling.
* **Text Preview:** The getDisplayText method is configured to return item.props.text.value, ensuring the actual heading text is visible in the component tree or layers panel for easy navigation.

**3. Usage & Flexibility**<br/>
* **Replacement:** Through replaceableWith, this component can be transformed into other typography types (like H2 or H4) without losing its configuration, facilitating rapid prototyping and refactoring.
* **Interaction:** The component supports standard interaction properties, including tooltips (referenced in the description), which allow developers to add helper text that appears on hover.

**4. Categorization**<br/>
* **Palette Location:** It is filed under the "Typography" category.
* **Iconography:** Uses the specific icon io-h3 (type: cloudio) to be instantly recognizable in the visual builder interface.`,
    helpRenders: helpRenders('H3'),
  };
  return mgr;
};

type H4 = 'H4';

const H4Manager = (platform: Platform, type: H4): ComponentManager<H4> => {
  const variant: Variant = 'h4';
  const mgr: ComponentManager<H4> = {
    ...baseComponentManager<H4>(platform, type),
    defaultValue: { ...defaultValue, variant },
    props,
    getDisplayText: (item) => item.props.text.value,
    name: type,
    category: 'Typography',
    icon: { iconName: 'io-h4', iconType: 'cloudio' },
    isPropertyVisible,
    replaceableWith,
    description: `**Overview**<br/>
The H4 component represents a Level 4 Heading. It is managed by the H4Manager and acts as a low-level section identifier. While the code's description property suggests generic header usage, in practice, H4 is typically used for titles of individual form groups, small content blocks, or widgets where a major section header (like H2 or H3) would be visually overwhelming.

**1. Typography & Styling**<br/>
* **Variant:** The manager explicitly sets const variant: Variant = 'h4', which is merged into the default values. This ensures the component automatically adopts the font size, weight, and spacing defined for H4 in the application's theme.
* **Default State:** By integrating defaultValue, the component comes pre-configured with standard settings (like alignment and color) while strictly enforcing the H4 visual style.

**2. Designer Experience**<br/>
* **Text Preview:** The getDisplayText function returns the value of the text property (item.props.text.value), allowing the structure of the page to be read directly from the component tree.

**3. Flexibility**<br/>
* **Interchangeability:** Defined in the replaceableWith array, this component can be swapped with other headers (H1-H6) or the generic Typography component. This allows developers to adjust the semantic outline of a page without deleting and re-configuring components.
* **Property Control:** It uses isPropertyVisible to manage the complexity of the property panel, showing or hiding data-binding fields (like comboDatasourceAlias) based on whether the user is configuring a static or dynamic label.`,
    helpRenders: helpRenders('H4'),
  };
  return mgr;
};

type H5 = 'H5';

const H5Manager = (platform: Platform, type: H5): ComponentManager<H5> => {
  const variant: Variant = 'h5';
  const mgr: ComponentManager<H5> = {
    ...baseComponentManager<H5>(platform, type),
    defaultValue: { ...defaultValue, variant },
    props,
    getDisplayText: (item) => item.props.text.value,
    name: type,
    category: 'Typography',
    icon: { iconName: 'io-h5', iconType: 'cloudio' },
    isPropertyVisible,
    replaceableWith,
    description: `**Overview**<br/>
The H5 component represents a Level 5 Heading. Managed by the H5Manager, it provides a subtle header that is visually distinct from body text but carries less weight than major section titles. It is ideal for labeling lists, small cards, or secondary information panels where a strong visual break is unnecessary.

**1. Typography & Styling**<br/>
* **Variant:** The manager enforces const variant: Variant = 'h5', ensuring that all H5 instances consistently use the platform's specific font size and weight for Level 5 headings.
* **Visual Balance:** By merging this variant into defaultValue, the component automatically renders with a style that is typically only slightly larger or heavier than standard text, making it suitable for dense information layouts.

**2. Data Integration**<br/>
* **Dynamic Labels:** Since it shares the common props definition, the H5 component is fully data-aware. It can be configured with a Data Store Alias to display dynamic records, making it useful for headers in repeating lists or grids (e.g., a list of product names where each name is an H5).

**3. Designer Experience**<br/>
* **Identification:** It is represented in the visual builder by the io-h5 icon (iconType: 'cloudio') and is located in the Typography category.
* **Usability:** The getDisplayText method enables the builder's outline view to show the actual text content of the header, helping developers navigate complex page structures easily.

**4. Flexibility**<br/>
* **Refactoring:** Like other headers, it includes the replaceableWith property, allowing it to be instantly converted to other heading levels (like H4 or H6) if the visual hierarchy of the page needs adjustment during development.`,
    helpRenders: helpRenders('H5'),
  };
  return mgr;
};

type H6 = 'H6';

const H6Manager = (platform: Platform, type: H6): ComponentManager<H6> => {
  const variant: Variant = 'h6';
  const mgr: ComponentManager<H6> = {
    ...baseComponentManager<H6>(platform, type),
    defaultValue: { ...defaultValue, variant },
    props,
    getDisplayText: (item) => item.props.text.value,
    name: type,
    category: 'Typography',
    icon: { iconName: 'io-h6', iconType: 'cloudio' },
    isPropertyVisible,
    replaceableWith,
    description: `**Overview**<br/>
The H6 component represents a Level 6 Heading, the lowest level in the heading hierarchy. Managed by the H6Manager, it is typically used for minor captions, subtitles, or labeling dense data fields where a semantic heading is required but heavy visual emphasis is not.

**1. Typography & Styling**<br/>
* **Variant:** The manager enforces const variant: Variant = 'h6', ensuring the component renders with the specific font size and line height defined for H6 in the application theme.
* **Visual Weight:** This is the smallest and most subtle heading style. It is often used for "eyebrow" text (small text above a main title) or for labeling secondary data points in a card or list.

**2. Layout & Truncation**<br/>
* **Space Management:** Due to its common usage in dense UI areas (like card footers or list sub-text), the Max Lines For No Wrap property is particularly useful here. It allows you to restrict the heading to 1 or 2 lines and truncate with an ellipsis if the text overflows, preserving the layout structure.

**3. Designer Experience**<br/>
* **Tree Navigation:** The getDisplayText method enables the builder's component tree to display the actual text of the heading, making it easier to locate specific labels within complex pages.

**4. Flexibility**<br/>
* **Interchangeability:** It supports the replaceableWith property, meaning it can be swapped for a larger heading (like H5) or a generic Typography component without losing its data bindings or text configuration.`,
    helpRenders: helpRenders('H6'),
  };
  return mgr;
};

type TypographyType = 'Typography';

const TypographyManager = (
  platform: Platform,
  type: TypographyType,
): ComponentManager<TypographyType> => {
  const variant: Variant = 'body1';
  const mgr: ComponentManager<TypographyType> = {
    ...baseComponentManager<TypographyType>(platform, type),
    defaultValue: { ...defaultValue, variant },
    props,
    getDisplayText: (item) => item.props.text.value,
    name: type,
    category: 'Typography',
    icon: { iconName: 'text' },
    isPropertyVisible,
    replaceableWith,
    description: `**Overview**<br/>
The Typography component is the fundamental building block for displaying text content in the application. Unlike the specialized Heading components (H1–H6), this component defaults to body1 style, making it the primary choice for standard paragraph text, descriptions, and data values.
Use this component for any text that does not require semantic heading tags, such as article bodies, instructional text, or grid cell data.

**1. Styling & Variants**<br/>
* **Flexible Variants:** While it defaults to body1 (standard body text), the Variant property allows you to manually change it to any style defined in the theme (e.g., caption, subtitle1, overline, or even header styles like h1 if semantic structure allows).
* **Rich Formatting:** It exposes a comprehensive suite of styling controls including Font Size, Font Weight (100-900), Font Family, Color, Align (left, right, center, justify), and Font Style (italic, oblique).

**2. Layout & Display**<br/>
* **No Wrap:** A boolean toggle to prevent text from breaking into a new line.
* **Truncation:** The Max Lines For No Wrap property limits the text to a specific number of lines (e.g., 3 lines) and adds an ellipsis (...) if the content exceeds that limit, which is essential for card UI designs.
* **Spacing:** The Paragraph property adds a bottom margin to the component, automatically spacing it out from subsequent text blocks for better readability.

**3. Data Integration**<br/>
This component is designed to handle both static and dynamic content:
* **Expressions:** The Text property accepts expressions, allowing you to concatenate strings or format dates (e.g., "Order #" + OrderID).
* **Data Stores:** You can bind the component to a Data Store Alias to display values directly from a database.
* **Lookups:** It supports Lookup Code and Value Attribute / Label Attribute pairings. This allows the component to automatically translate a stored ID (like "NY") into a user-friendly label ("New York") without extra coding.

**4. Migration & Compatibility**<br/>
* **Legacy Support:** The manager includes an onReplace logic specifically for migrating from the older Text component. It automatically maps legacy alignment values (start → left, end → right) to ensure designs remain intact during upgrades.
* **Interchangeability:** It is part of the replaceableWith group, meaning it can be instantly converted into an H1–H6 component if a piece of text needs to be promoted to a heading later in the design process.`,
    helpRenders: helpRenders('Typography'),
    onReplace: (item: TypographyItem, fromItem: TextItem) => {
      if (fromItem.type === 'Text') {
        if (fromItem.props.align === 'unset') {
          item.props.align = undefined;
        }
        if (fromItem.props.align === 'end') {
          item.props.align = 'right';
        }
        if (fromItem.props.align === 'start') {
          item.props.align = 'left';
        }
      }
      return item;
    },
  };
  return mgr;
};
export {
  H1Manager,
  H2Manager,
  H3Manager,
  H4Manager,
  H5Manager,
  H6Manager,
  TypographyManager,
};
